/**
 * La pestaña «Torneos»: entra directo a los del club de la persona.
 *
 * NO ES UNA PANTALLA, ES UN DESVÍO. Los torneos siempre son DE UN CLUB —la
 * URL lo dice, y pedir «los torneos» sin decir de cuál club no significa
 * nada—, así que esta pestaña resuelve la única pregunta que falta: de cuál.
 *
 * La respuesta es el club por defecto, que se elige en «Más». Quien no eligió
 * todavía ve acá sus clubes para entrar de una, con el aviso de dónde se
 * arregla para que la próxima vez sea directo. Sin esto, la pestaña a la que
 * más se entra tendría un toque de más todas las veces.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Community, CommunityTournamentsSummary } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { OpenTournaments } from '../components/OpenTournaments';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function TournamentsTab(): JSX.Element {
  const { t } = useTranslation();
  const [communities, setCommunities] = useState<Community[] | null>(null);
  /**
   * `null` = TODAVÍA NO SE SABE, y es distinto de una lista vacía.
   *
   * Sin esa diferencia, «no cargó» y «no hay nada» se dibujan igual: la
   * pantalla queda sin etiquetas y ordenada alfabéticamente, que es
   * exactamente lo que se ve cuando todo funciona y ningún club tiene
   * torneos. Javier miró esa pantalla y dijo «sigo sin poder ver si tienen
   * torneos»; no había forma de saber si le faltaba el dato o le faltaban los
   * torneos.
   */
  const [resumen, setResumen] = useState<CommunityTournamentsSummary[] | null>(null);
  const [resumenFalló, setResumenFalló] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * ACÁ HABÍA UN DESVÍO AUTOMÁTICO al club marcado como «Mi club», y se fue
   * junto con ese ajuste. Se sentía como que la pestaña estaba rota: se
   * tocaba «Torneos» y aparecía la pantalla de un club, sin explicación. Y
   * quien entraba a un segundo club no tenía cómo darse cuenta de por qué la
   * pestaña seguía llevándolo al primero.
   */
  const load = useCallback(async () => {
    try {
      setCommunities(await api.listMyCommunities());
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
    /*
     * EL RESUMEN VA APARTE Y NO ROMPE LA PANTALLA SI FALLA: la lista de
     * clubes sigue siendo la lista, con su botón para entrar. Un cartel rojo
     * encima de tres clubes que sí cargaron haría creer que todo está roto.
     *
     * PERO SÍ SE DICE. Callarlo dejaba la pantalla idéntica a «ninguno de tus
     * clubes tiene torneos», que es una afirmación que nadie comprobó — y la
     * más cara de las dos, porque manda a la persona a no entrar a ningún
     * lado. Una línea gris alcanza.
     */
    try {
      const filas = await api.myCommunitiesSummary();
      setResumen(Array.isArray(filas) ? filas : []);
      setResumenFalló(false);
    } catch {
      setResumen(null);
      setResumenFalló(true);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const activos = (communities ?? []).filter((c) => c.myMembershipStatus === 'ACTIVE');
  const porClub = new Map((resumen ?? []).map((r) => [r.communityId, r]));

  /*
   * PRIMERO DONDE ME PUEDO ANOTAR. La pregunta que trae quien mira esta
   * pantalla no es «¿cómo se llaman mis clubes?» —eso ya lo sabe— sino «¿dónde
   * hay algo para mí?», y el orden de llegada de las membresías no contesta
   * nada.
   *
   * El orden: donde me puedo anotar, después donde hay inscripciones abiertas
   * aunque yo no entre, después donde hay algo en curso para mirar, y de
   * últimos los que no tienen nada. Entre iguales, por nombre —y no por el
   * orden en que vinieron— para que la lista no se reacomode sola entre dos
   * visitas.
   */
  const míos = [...activos].sort((a, b) => {
    const peso = (c: Community): number => {
      const r = porClub.get(c.id);
      if (r === undefined) return 0;
      if (r.canEnter > 0) return 3;
      if (r.openForEntry > 0) return 2;
      if (r.inProgress > 0) return 1;
      return 0;
    };
    return peso(b) - peso(a) || a.name.localeCompare(b.name);
  });

  return (
    <AppLayout>
      <h1>{t('tournaments.title')}</h1>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {communities === null ? (
        <p className="help">{t('common.loading')}</p>
      ) : activos.length === 0 ? (
        <>
          <p className="help">{t('tournaments.noCommunitiesYet')}</p>
          <p className="links">
            <Link className="button-like" to="/clubes">
              {t('nav.communities')}
            </Link>
          </p>
        </>
      ) : (
        <>
          <p className="help">{t('tournaments.pickCommunity')}</p>
          {resumenFalló && <p className="help">{t('tournaments.summaryFailed')}</p>}
          <ul className="member-list">
            {míos.map((community) => (
              <li key={community.id}>
                <div className="member-name">
                  <strong>{community.name}</strong>
                  <span className="help">{community.locationText}</span>
                  <QuéHay resumen={porClub.get(community.id)} />
                </div>
                <Link className="button-like" to={`/clubes/${community.id}/torneos`}>
                  {t('tournaments.open')}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* LA VITRINA TAMBIÉN ACÁ, y acá es donde más sirve: quien todavía no
          eligió club —o no está en ninguno— es justamente el que no tiene
          cómo enterarse de que hay torneos. Sin excepción de club: no está
          mirando ninguno. */}
      <OpenTournaments />
    </AppLayout>
  );
}

/**
 * QUÉ HAY EN ESE CLUB, en una línea de etiquetas.
 *
 * NADA MIENTRAS NO SE SABE. El resumen viaja aparte de la lista de clubes, así
 * que hay un instante sin él —y puede no llegar nunca, si esa consulta falla—.
 * Dibujar «0 en inscripción» en ese momento sería afirmar algo que no se
 * comprobó, y encima lo contrario de lo que suele ser cierto.
 *
 * «PODÉS ANOTARTE» REEMPLAZA AL CONTEO, no se suma. Quien puede anotarse ya no
 * necesita saber en cuántos: lo que va a hacer es entrar.
 */
function QuéHay({ resumen }: { resumen: CommunityTournamentsSummary | undefined }): JSX.Element | null {
  const { t } = useTranslation();
  if (resumen === undefined) return null;

  const { canEnter, openForEntry, inProgress } = resumen;
  if (canEnter === 0 && openForEntry === 0 && inProgress === 0) {
    return <span className="help">{t('tournaments.nothingHere')}</span>;
  }

  return (
    <span className="community-tags">
      {canEnter > 0 ? (
        <span className="tag tag-ok">{t('tournaments.canEnterCount', { count: canEnter })}</span>
      ) : (
        openForEntry > 0 && (
          <span className="tag">{t('tournaments.openCount', { count: openForEntry })}</span>
        )
      )}
      {inProgress > 0 && (
        <span className="tag">{t('tournaments.runningCount', { count: inProgress })}</span>
      )}
    </span>
  );
}
